import Icon from '@mdi/react';
import { pdsTv } from '@nestle-npus-nbm/pds-ui';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
} from '@radix-ui/react-navigation-menu';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import React, { useState } from 'react';

import { mdiChevronDown } from 'src/assets/icons/mdiIcons';
import { NerdMenuElementType } from 'src/common/enums';
import { BaseLink } from '../Link';
import NerdMenuElements from './NerdMenuElements';

interface NerdMenuBarProps {
  nerdMenu: NerdMenu;
  className?: string;
}

const triggerAndLinkStyles = pdsTv({
  base: [
    'pds-mx-4.5 pds-text-2xl pds-font-light pds-text-surface-text pds-no-underline',
    'focus-visible:pds-rounded-sm focus-visible:pds-outline focus-visible:pds-outline-2 focus-visible:pds-outline-offset-2 focus-visible:pds-outline-focus',
  ],
});

const NerdMenuBar: React.FC<NerdMenuBarProps> = function ({ nerdMenu, className }) {
  const { menuElements } = nerdMenu;

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const menuItems = menuElements.filter(x => {
    return x.type === 'mega_menu_dropdown' || x.type === 'mega_menu_link';
  });

  if (menuItems.length === 0) {
    // eslint-disable-next-line no-console
    console.error('There are no top-level menu items provided. Please update the menu config.');
    return null;
  }

  return (
    <div className={className}>
      <NavigationMenu asChild aria-label={undefined}>
        <div>
          <NavigationMenuList className="p-0 m-0 pds-flex">
            {menuItems.map(menuElement => {
              const isActive = activeDropdown === menuElement.drupal_id;
              return (
                <NavigationMenuItem value={menuElement.drupal_id} key={menuElement.drupal_id}>
                  {/* Links */}
                  {menuElement.type === NerdMenuElementType.MEGA_MENU_LINK && (
                    <NavigationMenuLink asChild>
                      <BaseLink
                        to={menuElement.link.url}
                        className={triggerAndLinkStyles({ className: 'pds-flex' })}
                      >
                        {menuElement.title}
                      </BaseLink>
                    </NavigationMenuLink>
                  )}

                  {/* Drop down*/}
                  {menuElement.type === NerdMenuElementType.MEGA_MENU_DROPDOWN && (
                    <>
                      <NavigationMenuTrigger
                        className={triggerAndLinkStyles({
                          className: 'pds-group pds-flex pds-items-center pds-gap-2',
                        })}
                        onPointerMove={e => e.preventDefault()}
                        onPointerLeave={e => e.preventDefault()}
                        onClick={() => {
                          setActiveDropdown(isActive ? null : menuElement.drupal_id);
                          sendIt({
                            event: 'nav_click',
                            eventParams: {
                              nav_item: menuElement.title,
                              nav_type: 'header',
                            },
                          });
                        }}
                        aria-expanded={isActive}
                      >
                        <div>{menuElement.title}</div>

                        <Icon
                          path={mdiChevronDown}
                          size={1}
                          className="pds-transition pds-duration-300 group-data-[state=open]:pds-rotate-180"
                          aria-hidden="true"
                        />
                      </NavigationMenuTrigger>

                      <NavigationMenuContent
                        className="pds-max-h-[calc(100vh-175px)] pds-overflow-auto pds-border-t-medium pds-border-t-surface-heavy pds-bg-white pds-px-4.5 pds-py-4 pds-shadow-weaker data-[state=closed]:pds-hidden"
                        style={{ display: isActive ? 'block' : 'none' }}
                        onPointerEnter={e => e.preventDefault()}
                        onPointerLeave={e => e.preventDefault()}
                        onInteractOutside={e => {
                          if (!e.target || !(e.target as Element).closest('[data-state=open]')) {
                            setActiveDropdown(null);
                          }
                        }}
                        onEscapeKeyDown={() => {
                          setActiveDropdown(null);
                        }}
                        data-active={isActive}
                        forceMount
                      >
                        <NerdMenuElements menuElement={menuElement} />
                      </NavigationMenuContent>
                    </>
                  )}
                </NavigationMenuItem>
              );
            })}
          </NavigationMenuList>

          <div className="pds-absolute pds-left-0 pds-right-0 pds-top-[calc(100%-15px)] pds-flex pds-justify-center">
            <NavigationMenuViewport className="pds-mx-4 pds-flex-grow pds-justify-center pds-overflow-hidden pds-bg-white pds-shadow-weaker" />
          </div>
        </div>
      </NavigationMenu>
    </div>
  );
};

export default NerdMenuBar;
